import { useState } from 'react';
import { useApi } from '../api.js';

function fmt(n) { return n == null ? '—' : Number(n).toLocaleString(); }
function fmtDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleDateString('en-CA') + ' ' + d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' });
  } catch { return s; }
}
function relTime(s) {
  if (!s) return '—';
  const d = new Date(s); const ms = Date.now() - d.getTime();
  if (ms < 60_000) return Math.round(ms / 1000) + 's ago';
  if (ms < 3600_000) return Math.round(ms / 60_000) + 'm ago';
  if (ms < 86_400_000) return Math.round(ms / 3_600_000) + 'h ago';
  return Math.round(ms / 86_400_000) + 'd ago';
}

export default function Usage() {
  const [days, setDays] = useState(7);
  const { data, error, loading, refresh } = useApi('/api/usage/report?days=' + days);

  if (loading && !data) return <div className="loading">Loading usage…</div>;
  if (error) return <div className="error">Error: {error}</div>;
  const d = data || { kpis: {}, dailyActive: [], users: [], topPages: [], recent: [] };

  // Find the max for the daily-active mini bar chart
  const dailyMax = Math.max(1, ...d.dailyActive.map(x => x.events));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Usage tracking</h2>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <span className="small" style={{ color: '#666' }}>Window:</span>
          {[1, 7, 30, 90].map(n => (
            <button key={n} className="refresh-btn"
                    onClick={() => setDays(n)}
                    style={{ background: days === n ? '#0369a1' : '#94a3b8' }}>
              {n === 1 ? 'Today' : n + 'd'}
            </button>
          ))}
          <button className="refresh-btn" onClick={refresh}>Reload</button>
        </div>
      </div>
      <div className="small" style={{ marginTop: '0.25rem', marginBottom: '0.8rem', color: '#666' }}>
        Page views + key actions across the dashboard. Last {days} day{days === 1 ? '' : 's'}.
      </div>

      {/* KPI cards */}
      <div className="cards">
        <div className="card">
          <div className="label">Total events</div>
          <div className="value">{fmt(d.kpis.totalEvents)}</div>
          <div className="sub">across {fmt(d.kpis.activeDays)} active day{d.kpis.activeDays === 1 ? '' : 's'}</div>
        </div>
        <div className="card">
          <div className="label">Unique users</div>
          <div className="value">{fmt(d.kpis.uniqueUsers)}</div>
          <div className="sub">in last {days} day{days === 1 ? '' : 's'}</div>
        </div>
        <div className="card">
          <div className="label">Top page</div>
          <div className="value" style={{ fontSize: '1.1rem' }}>{d.kpis.topPage || '—'}</div>
          <div className="sub">{d.topPages[0] ? fmt(d.topPages[0].count) + ' views' : ''}</div>
        </div>
      </div>

      {/* Per-user table */}
      <div className="chart-card" style={{ marginTop: '1rem' }}>
        <h3>Users — events per person</h3>
        <table className="datatable" style={{ width: '100%', fontSize: '0.9em' }}>
          <thead><tr>
            <th>User</th>
            <th className="num">Events</th>
            <th className="num">Active days</th>
            <th>Last seen</th>
            <th>Top pages</th>
          </tr></thead>
          <tbody>
            {d.users.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888', padding: '1.5rem' }}>No activity yet — usage tracking starts collecting on the first page navigation after schema-013 is applied.</td></tr>
            )}
            {d.users.map(u => (
              <tr key={u.email}>
                <td><strong>{u.email}</strong></td>
                <td className="num">{fmt(u.events)}</td>
                <td className="num">{fmt(u.uniqueDays)}</td>
                <td className="small" title={fmtDate(u.lastSeen)}>{relTime(u.lastSeen)}</td>
                <td className="small">
                  {(u.topPages || []).slice(0, 3).map((p, i) => (
                    <span key={p.page} style={{ marginRight: 8 }}>
                      <code>{p.page}</code> <span style={{ color: '#888' }}>({p.count})</span>
                      {i < Math.min(2, u.topPages.length - 1) && ' · '}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel-row" style={{ marginTop: '1rem' }}>
        {/* Daily active mini chart */}
        <div className="panel">
          <h3>Daily activity</h3>
          <table style={{ width: '100%', fontSize: '0.85em' }}>
            <tbody>
              {d.dailyActive.length === 0 && <tr><td style={{ color: '#888', padding: '0.5rem' }}>No data.</td></tr>}
              {d.dailyActive.map(row => (
                <tr key={row.date}>
                  <td style={{ width: '8em' }}><code>{row.date}</code></td>
                  <td style={{ width: '4em' }} className="num">{fmt(row.users)} <span className="small" style={{ color: '#666' }}>user{row.users === 1 ? '' : 's'}</span></td>
                  <td style={{ position: 'relative', height: 22 }}>
                    <div style={{
                      position: 'absolute', left: 0, top: 4, height: 14,
                      width: ((row.events / dailyMax) * 100).toFixed(1) + '%',
                      background: '#16a34a', borderRadius: 3
                    }} />
                    <span style={{ position: 'absolute', left: 6, top: 3, fontSize: '0.78em', color: '#fff', fontWeight: 600 }}>{fmt(row.events)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Page popularity */}
        <div className="panel">
          <h3>Top pages</h3>
          <table style={{ width: '100%', fontSize: '0.85em' }}>
            <thead><tr><th>Page</th><th className="num">Views</th><th className="num">Users</th></tr></thead>
            <tbody>
              {d.topPages.length === 0 && <tr><td colSpan={3} style={{ color: '#888', padding: '0.5rem' }}>No data.</td></tr>}
              {d.topPages.map(p => (
                <tr key={p.page}>
                  <td><code>{p.page}</code></td>
                  <td className="num">{fmt(p.count)}</td>
                  <td className="num">{fmt(p.uniqueUsers)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Live feed */}
      <div className="chart-card" style={{ marginTop: '1rem' }}>
        <h3>Recent events <span className="small" style={{ color: '#666' }}>(last 50)</span></h3>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.85em' }}>
            <thead><tr><th>When</th><th>User</th><th>Type</th><th>Page</th><th>Detail</th></tr></thead>
            <tbody>
              {d.recent.length === 0 && <tr><td colSpan={5} style={{ color: '#888', padding: '0.5rem' }}>No data.</td></tr>}
              {d.recent.map((e, i) => (
                <tr key={i}>
                  <td className="small" title={fmtDate(e.happenedAt)}>{relTime(e.happenedAt)}</td>
                  <td className="small">{e.email}</td>
                  <td className="small"><code>{e.eventType}</code></td>
                  <td className="small"><code>{e.page || ''}</code></td>
                  <td className="small">{e.detail || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
